import { Container, Skeleton, Stack } from "@mui/material";
import title from "global/constants/TitleConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useLocation } from "react-router-dom";
import { useTitle } from "utils/UseTitle";
import Step2 from "./components/Step2";
import Step1 from "./components/Step1";
import { useEffect, useState } from "react";
import { SubmitCampaign } from "types/Campaign";
import { initialSubmitCampaignFields } from "../Constants";
import { CampaignContext } from "../Context";
import { handleErrorMessage } from "helpers/methods";
import {
  draftABCampaign,
  fetchApprovedDomains,
  getDraftABCampaignDetails,
} from "screens/Campaigns/CampaignService";

const ABTestCampaign = () => {
  const urlParams = new URLSearchParams(useLocation().search);
  const campaignId = urlParams.get("id");
  useTitle(campaignId ? title.UPDATE_AB_TEST_CAMPAIGN : title.CREATE_AB_TEST_CAMPAIGN);
  const [submitCampaign, setSubmitCampaign] = useState<SubmitCampaign>(
    initialSubmitCampaignFields("ABTesting") as SubmitCampaign
  );
  const [skipStepOne, setSkipStepOne] = useState<boolean>(false);
  const [managingStep, setManagingStep] = useState<string>("");
  const [loadingCampaignInfo, setLoadingCampaignInfo] =
    useState<boolean>(false);

  useEffect(() => {
    if (campaignId) {
      retrieveCampaignDetails();
    } else {
      updateDomainDetails();
    }
  }, [campaignId]);

  const updateDomainDetails = async () => {
    try {
      const response = await fetchApprovedDomains();
      if (response.length === 1) {
        setSubmitCampaign({
          ...submitCampaign,
          campaignData: {
            ...submitCampaign.campaignData,
            domainName: response[0],
          },
        });
      }
      return response;
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const retrieveCampaignDetails = async () => {
    try {
      setLoadingCampaignInfo(true);
      const response = await getDraftABCampaignDetails(parseInt(campaignId!));
      const campaign = initialSubmitCampaignFields("AB Testing", response);

      if (!campaign.campaignData?.domainName) {
        const domain = await updateDomainDetails();
        campaign.campaignData.domainName = domain[0];
      }
      setSubmitCampaign(campaign);
      setLoadingCampaignInfo(false);
    } catch (error: any) {
      setLoadingCampaignInfo(false);
      handleErrorMessage(error);
    }
  };

  const getSteps = () => {
    if (loadingCampaignInfo) {
      return (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Skeleton animation="wave" variant="text" height={45} width={200} />
          </Stack>
          <Skeleton animation="wave" variant="rounded" height={125} />
          <Skeleton animation="wave" variant="rounded" height={125} />
          <Skeleton animation="wave" variant="rounded" height={125} />
          <Skeleton animation="wave" variant="rounded" height={125} />
        </Stack>
      );
    }
    if (skipStepOne) {
      return <Step2 setSkipStepOne={setSkipStepOne} />;
    } else {
      return <Step1 setSkipStepOne={setSkipStepOne} />;
    }
  };

  const saveAsDraft = async (submitCampaign: SubmitCampaign) => {
    try {
      const campaignId =
        submitCampaign.campaign.status === "Draft" &&
        (await draftABCampaign(submitCampaign));
      return campaignId;
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  return (
    <CampaignContext.Provider
      value={{
        submitCampaign,
        setSubmitCampaign,
        managingStep,
        setManagingStep,
        saveAsDraft,
      }}
    >
      <CenteredAuthenticatedLayout>
        <Container maxWidth="md">{getSteps()}</Container>
      </CenteredAuthenticatedLayout>
    </CampaignContext.Provider>
  );
};

export default ABTestCampaign;
